/**
 * API service for email subscription
 * Handles the communication with the backend for subscribing users
 */

/**
 * Interface for subscription data
 */
export interface SubscriptionData {
  email: string;
  firstName: string;
  lastName: string;
}

/**
 * Interface for the subscription API response
 */
export interface SubscriptionResponse {
  message: string;
}

/**
 * Subscribe a user to the newsletter
 * 
 * @param data - The subscription data including email, firstName, and lastName
 * @param returnUrl - The URL to return to after confirmation
 * @returns Promise with the API response
 */
export const subscribeEmail = async (
  data: SubscriptionData,
  returnUrl: string
): Promise<SubscriptionResponse> => {
  try {
    // Get CSRF token first (following project pattern)
    const csrfResponse = await fetch('/api/csrf-token');
    if (!csrfResponse.ok) {
      throw new Error('Failed to obtain CSRF token');
    }

    // Get the CSRF token as plain text
    const csrfToken = await csrfResponse.text();

    // Make subscription request
    const response = await fetch('/api/subscribe/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrfToken,
      },
      body: JSON.stringify({
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        returnUrl
      }),
      credentials: 'include', // Include cookies for CSRF
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to subscribe');
    }

    return response.json();
  } catch (error) {
    console.error('Subscription API error:', error);
    throw error;
  }
};